/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useRef, useState, useReducer, createContext, ReactElement, useMemo } from 'react';
import { ComponentProps, PageProps } from './index.types';
import SearchDesktop from './organisms/searchDesktop/searchDesktop';
import SearchMobile from './organisms/searchMobile/searchMobile';
import CommonUtils from '../utils/CommonUtils';
import {
  ViewportContext,
  ContentContext,
  FormValuesContext,
  FocusContext,
  SuggestedPlaceDetailsContext,
  SelectDestinationNameContext,
} from './index.contexts';
import { storeReducer, getInitialStoreValues, setInitialStoreValues } from '../store/store';
import {
  setDates,
  setDestination,
  setDestinationHiddenFields,
  setRoomsAndGuests,
  setSpecialRates,
  setUsePoints,
} from '../store/store.actions';
import {
  DESTINATION,
  DATES,
  ROOMS_AND_GUESTS,
  USE_POINTS,
  SPECIAL_RATES,
  DESTINATION_HIDDEN_FIELDS,
  MAP_VIEW_SESSION_KEY,
} from '../constants/StoreConstants';
import {
  SEARCH_SCROLL_TOP_POSITION,
  SEARCH_STATE_COOKIE,
  PUBLISH_USER_SELECTION,
  SEARCH_FORM_WIDTH,
} from '../constants/ApplicationConstants';
import { resetForm } from '../store/store.actions';
import { v4 as uuidv4 } from 'uuid';
import { convertMetersToMiles, flattenDataFromSession, updateRecentSearched } from '../utils/Helpers';
import ScrollUtils from '../utils/ScrollUtils';
import { returnUnescapedString } from '../utils/CommonUtils';
import { DiffBtDates } from '@marriott/mi-ui-library-shop';
import moment from 'moment';
import { useConditionalPageContextSERP } from '../../../../hooks';
import { pagePropsMockData } from '../__localDevMocks/page-props';
import { StoreValuesType } from '../store/store.types';
import { useApolloClient } from '@apollo/client';
import { phoenixShopSuggestedPlacesDetails } from '../../../../molecules/SearchField/queries';
import { getSearchDestinationTypeAnalytics, logger } from '../../../../utils';
import { inspect } from 'util';
import { ErrorHandlerAlert, PageonModal } from '../../../../molecules';
import { useHQVContext } from '../../../../context/HQVContext';
import { HQV_ACTIONS } from '../../../../reducers';
import { constants, LEISURE_REGION_PIDS_TO_SUPRESS } from '../../../../constants';
import { eventUtil } from '@marriott/mi-ui-library-shop';
import { useCheckBreakpoint } from '../../../../hooks/lib/useCheckBreakpoint';
import {
  getItemFromPageModel,
  getProcessEnvs,
  getSessionIDFromCookie,
  respGridUtil,
  getMockPageModel,
} from '../../../../utils/src/helper';
import { toRem } from '@marriott/mi-ui-library';
import { PROPERTY_INFO_DATA } from '../../../../modules/constants';
import { useSearchResultsStore } from '../../../../modules/store/searchResultsStore';
import { isDestinationTypeFlagEnabled } from '../../../../modules/utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { useSearchFormRenderedDataStore } from '../../../../modules/store/searchFormRenderedDataStore';
import { useSearchFormStore } from '../../../../modules/store/searchFormStore';

import {
  isConcurFlowActive,
  extractConcurCode,
  getSpecialRatesForSearch,
  hasConcurUserOverride,
  clearConcurUserOverride,
} from '../../../../modules/utils/searchFormHelper';
import { useGetUserInfo } from '../../../../hooks/lib/useGetUserInfo';
import { useUserDetailsGlobalStore } from '@marriott/mi-store-utils';

const { ROUTING_CONFIG, APPLICATION_NAME, EXPERIENCE_SEGMENT, HQV_SEOURL, ITEM_PATH_ROOT, PAGE_NAME, SPA_APP_NAME } =
  constants;
const { NEXT_PUBLIC_AEM_PATH } = getProcessEnvs();

const { log } = logger({})('SearchForm');

export const renderedData = createContext({
  nightCount: 1,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setNightCount: (_count: number) => {},
  monthSelected: 0,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMonthSelected: (_month: number) => {},
  yearSelected: 2022,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setYearSelected: (_year: number) => {},
  tabSelected: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setTabSelected: (_tabSelected: string) => {},
});

const SearchFormComponent: FC<ComponentProps> = ({ pageProps, setSearchLoadForm }) => {
  const { requestId } = useConditionalPageContextSERP();
  /* States */
  const [isMobileState, setIsMobile] = useState(CommonUtils.isOnlyMobileView());
  const [formValues, setFormValues] = useReducer(storeReducer, getInitialStoreValues());
  const [UUID, setUUID] = useState(uuidv4());
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [focusComp, setFocusComp] = useState('');
  const [isUserInteractWithComp, setIsUserInteractWithComp] = useState(false);
  const [isUserEditSearchForm, setIsUserEditSearchForm] = useState(false);
  const [isUpdateSearchClicked, setIsUpdateSearchClicked] = useState(false);
  const scrollPosition = useRef<number>(0);
  const concurCodeAlreadyApplied = useRef<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [suggestedPlaceDetailsCallDone, setSuggestedPlaceDetailsCallDone] = useState(true);
  const [isAutoSuggestSelected, setIsAutoSuggestSelected] = useState(false);
  const [isRecentSearchSelected, setIsRecentSearchSelected] = useState(false);
  const [isOnLoad, setIsOnLoad] = useState(true);
  const { envVarsObject } = useClientEnvVarsStore();
  const GET_DESTINATION_TYPE_FLAG = envVarsObject?.['GET_DESTINATION_TYPE_FLAG'] ?? false;

  // Get SearchForm renderedData store for initialization
  const searchFormRenderedDataStore = useSearchFormRenderedDataStore();

  // Get SearchForm store for initialization
  const searchFormStore = useSearchFormStore();

  // Get feature flags to check if we should initialize the Zustand store
  const featureFlags = envVarsObject?.['featureFlags'] as unknown as Record<string, FLAG_TYPE> | undefined;
  const userDetailsState = useUserDetailsGlobalStore();

  const {
    sessionData,
    currentLocale,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    hotelQuickViewCacheConfig,
    operationSignatures,
    isBulgariDomain,
    pageState,
    isAuthorMode,
  } = useConditionalPageContextSERP();

  // Ref to track if shouldUpdate has been true once
  // Update search form state with Concur code when user details are loaded
  useEffect(() => {
    if (!isConcurFlowActive(featureFlags, sessionData)) return;
    if (!userDetailsState?.isUserDetailsSaved) return;

    // Only apply Concur code once per search session
    if (concurCodeAlreadyApplied.current) {
      return;
    }

    // Check if user has manually overridden Concur in this session
    if (hasConcurUserOverride()) {
      concurCodeAlreadyApplied.current = true; // Mark as handled to prevent reprocessing
      return;
    }

    const concurCode = extractConcurCode(userDetailsState); // Extract on-the-fly
    const currentSpecialRates = formValues[SPECIAL_RATES];
    const currentUsePoints = formValues[USE_POINTS];

    const resolved = getSpecialRatesForSearch(
      currentSpecialRates?.clusterCode, // This is the specialRateCode in context API
      currentSpecialRates?.corporateCode,
      concurCode,
      currentUsePoints?.value,
      staticContent?.specialRatesListItems || []
    );

    // Update form if values differ
    if (
      currentSpecialRates?.displayText !== resolved.clusterCode ||
      currentSpecialRates?.clusterCode !== resolved.specialRateCode ||
      currentSpecialRates?.corporateCode !== resolved.corporateCode
    ) {
      setFormValues(
        setSpecialRates({
          displayText: resolved.clusterCode, // Label
          clusterCode: resolved.specialRateCode, // Code
          corporateCode: resolved.corporateCode,
        })
      );
      // Mark that we've applied Concur code
      concurCodeAlreadyApplied.current = true;
    }
  }, [userDetailsState?.isUserDetailsSaved, featureFlags, sessionData?.authenticated]);

  // Clear override flag on sign-out
  useEffect(() => {
    const sessionObject = sessionData?.data ? sessionData.data : sessionData?.cacheData?.data;
    if (sessionObject?.AriesCommon?.memState !== 'authenticated') {
      clearConcurUserOverride();
    }
  }, [sessionData]);

  // Reset Concur application flag when user starts a new search
  useEffect(() => {
    if (isUpdateSearchClicked) {
      concurCodeAlreadyApplied.current = false;
    }
  }, [isUpdateSearchClicked]);

  const skipQuery = useMemo(() => {
    return !!pageState?.authorMode || !!isAuthorMode;
  }, [pageState, isAuthorMode]);

  // these labels are required for SSR
  // Memoize staticContent to prevent new object reference on every render,
  // which would cause infinite re-renders when used in useEffect dependency arrays
  const staticContent: PageProps = useMemo(() => {
    const content = { ...pagePropsMockData, ...pageProps?.model };
    content.isItForOffersPage = content.isItForOffersPage === 'true';
    return content;
  }, [pageProps?.model]);

  // Initialize FocusContext state in SearchForm store when harness flag is enabled
  React.useEffect(() => {
    const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

    if (shouldUseZustand) {
      // Initialize focus context state with current React state values
      searchFormStore.setFocusComp(focusComp);
      searchFormStore.setIsUserInteractWithComp(isUserInteractWithComp);
      searchFormStore.setIsUserEditSearchForm(isUserEditSearchForm);
      searchFormStore.setIsUpdateSearchClicked(isUpdateSearchClicked);
    }
    // IMPORTANT: Don't include searchFormStore in dependency array to avoid infinite loops
  }, [focusComp, isUserInteractWithComp, isUserEditSearchForm, isUpdateSearchClicked, featureFlags]);

  // Request body for AEM template call in which SEOURL will decide which template to load
  const getCMSTemplateReqBody = {
    requestType: ROUTING_CONFIG,
    seoUrl: HQV_SEOURL,
    localeKey: currentLocale,
    experienceSegment: EXPERIENCE_SEGMENT,
    applicationName: SPA_APP_NAME,
    sessionToken: '',
  };
  let sessionToken = '';
  let formData: StoreValuesType;
  const { hqvDispatch, hqvState, setModalOpenState, setPropertyID } = useHQVContext();
  // get session information
  if (sessionData && (sessionData.data || sessionData.cacheData)) {
    const sessionObj = sessionData.data ? sessionData : sessionData.cacheData;
    sessionToken = sessionObj?.data?.sessionToken || sessionObj?.key || getSessionIDFromCookie() || '';
    getCMSTemplateReqBody.sessionToken = sessionToken;
    log.debug('[Debug] - Session Token Fetched - ', sessionToken);
    const sessionObject = sessionData.data ? sessionData.data : sessionData.cacheData.data;
    formData = flattenDataFromSession(
      sessionObject,
      staticContent?.enableAdfSearchForm,
      userDetailsState,
      staticContent?.['specialRatesListItems'] || [],
      featureFlags
    );
  } else {
    log.warn('[Debug] - Session Token Unavailable');
    formData = {};
  }
  // Always call the hook - it handles conditional logic internally

  useGetUserInfo({
    sessionToken: isConcurFlowActive(featureFlags, sessionData) ? sessionToken : undefined,
    requestId,
    currentLocale,
  });
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const client = useApolloClient();
  async function getSuggestedPlaceDetails(inputValues: any) {
    const { data, error } = await client.query({
      query: phoenixShopSuggestedPlacesDetails,
      ...inputValues,
    });
    if (data) {
      // Update both context and store
      setSuggestedPlaceDetailsCallDone(true);

      const pid = formValues?.destination?.pid?.toString() ?? '';
      if (LEISURE_REGION_PIDS_TO_SUPRESS.includes(pid)) {
        if (data?.suggestedPlaceDetails?.location) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { latitude, longitude, ...locationWithoutCoords } = data.suggestedPlaceDetails.location;
          data.suggestedPlaceDetails = {
            ...data.suggestedPlaceDetails,
            location: locationWithoutCoords,
          };
        }
        // eventUtil.dispatch('setStateSearchTrue', () => {});
      }
      setFormValues(
        setDestinationHiddenFields({
          ...data?.suggestedPlaceDetails?.location,
          radius: convertMetersToMiles(data?.suggestedPlaceDetails?.distance),
          destinationType: isDestinationTypeFlagEnabled(GET_DESTINATION_TYPE_FLAG)
            ? data?.suggestedPlaceDetails?.destinationType ?? ''
            : getSearchDestinationTypeAnalytics(data?.suggestedPlaceDetails?.types),
        })
      );
    }
    if (error) {
      setSuggestedPlaceDetailsCallDone(true);
      log.debug('error with suggested placed details api', error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [nightCount, setNightCount]: any = useState<number>(
    formData?.dates?.lengthOfStay ? formData?.dates?.lengthOfStay : 1
  );

  //to display the tab based on session & AEM flags
  const [tabSelected, setTabSelected]: any = useState<string>(
    staticContent?.enableAdfSearchForm ? staticContent['flexibleTabText'] : staticContent['exactDatesTabText']
  );
  const checkInDateFromSession = sessionData?.data
    ? sessionData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkInDate
    : sessionData?.cacheData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkInDate;
  //TODO - remove eslint-disable statement in future
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [monthSelected, setMonthSelected]: any = useState<number>(
    Number(
      (
        moment(checkInDateFromSession).subtract(moment(checkInDateFromSession).utcOffset() / 60, 'hours') ?? moment()
      ).format('M')
    ) - 1
  );
  //TODO - remove eslint-disable statement in future
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [yearSelected, setYearSelected]: any = useState(
    Number(moment(formData?.dates?.fromDate)?.format('YYYY'))
      ? Number(moment(formData?.dates?.fromDate)?.format('YYYY'))
      : Number(moment().format('YYYY'))
  );

  // Initialize SearchForm renderedData Zustand store
  React.useEffect(() => {
    const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

    if (shouldUseZustand) {
      // Initialize the store with current flexible search values
      const storeData = {
        nightCount,
        monthSelected,
        yearSelected,
        tabSelected,
      };

      if (searchFormRenderedDataStore.isInitialState()) {
        // Initialize store if in default state
        searchFormRenderedDataStore.initializeSearchFormRenderedData(storeData);
      }
    }
  }, [nightCount, monthSelected, yearSelected, tabSelected, featureFlags]);

  //to not to call suggestedPlaceDetails on page load from HP to SERP
  const [shouldCallSuggestedPlaceDetails, setShouldCallSuggestedPlaceDetails] = useState(false);

  const flexibleSearchValues = {
    nightCount,
    setNightCount,
    monthSelected,
    setMonthSelected,
    yearSelected,
    setYearSelected,
    tabSelected,
    setTabSelected,
  };
  const setCookieValues = (): void => {
    const getAllCookies = document.cookie.split('=');
    const getSFCookieIndex = getAllCookies.findIndex(i => i === SEARCH_STATE_COOKIE);
    if (getSFCookieIndex >= 0) {
      const getSFCookieValue = getAllCookies[getSFCookieIndex + 1].split(';')[0];
      const getDestinationValue = JSON.parse(getSFCookieValue).destination;
      let getSearchValues = JSON.parse(getSFCookieValue).searchDetails;
      if (getSearchValues?.dates?.fromDate) {
        const addVal = 1000 * 60 * 60 * 24;
        getSearchValues = {
          ...getSearchValues,
          dates: {
            ...getSearchValues.dates,
            fromDate:
              DiffBtDates(getSearchValues.dates.fromDate.substr(0, 10)) > 0
                ? moment(getSearchValues.dates.fromDate)
                : moment(Date.now()),
            toDate:
              DiffBtDates(getSearchValues.dates.toDate.substr(0, 10)) > 1
                ? moment(getSearchValues.dates.toDate)
                : moment(Date.now() + addVal),
          },
        };
        let destination = '';
        if (window?.destinationMetaDataDetails) {
          const { destinationName } = window.destinationMetaDataDetails;
          destination = returnUnescapedString(`${destinationName}`);
        }
        if (returnUnescapedString(getDestinationValue) === returnUnescapedString(destination)) {
          setFormValues(
            setDates({
              ...(getSearchValues?.[DATES] ?? {}),
              ...getSearchValues.dates,
            })
          );

          setFormValues(
            setRoomsAndGuests({
              ...(getSearchValues?.[ROOMS_AND_GUESTS] ?? {}),
              ...getSearchValues.roomsAndGuests,
            })
          );

          setFormValues(
            setUsePoints({
              ...(getSearchValues?.[USE_POINTS] ?? {}),
              ...getSearchValues.usePoints,
            })
          );

          setFormValues(
            setSpecialRates({
              ...(getSearchValues?.[SPECIAL_RATES] ?? {}),
              ...getSearchValues.specialRates,
            })
          );
        }
      }
    }
  };
  /**
   * get default values from staticContent
   */
  const getDefaultValues = (): Record<string, unknown> => {
    let defaultValues = {};
    // enableRedeemPoints configured as true then pass [USE_POINTS] = true
    if (staticContent?.['enableRedeemPoints'] === 'true') {
      defaultValues = { ...defaultValues, [USE_POINTS]: { value: true } };
    } else {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { promoRateCode, specialRateslabel, specialRatesCode } =
        staticContent?.['specialRatesListItems']?.filter(
          (item: { specialRatesCode: string }) => item.specialRatesCode === 'corp'
        )[0] || {};
      // if promoRateCode is configured then set [SPECIAL_RATES] default values as 'Corp/Promo'
      if (promoRateCode) {
        defaultValues = {
          ...defaultValues,
          [SPECIAL_RATES]: {
            displayText: specialRateslabel,
            clusterCode: specialRatesCode,
            corporateCode: promoRateCode,
          },
        };
      }
    }
    return defaultValues;
  };

  /**
   * Effects
   */
  useEffect(() => {
    window.addEventListener('load', function () {
      // load dependency JS when component is mounted
      // CommonUtils.injectDependency(window.__webpack_SearchForm_path__);
      // load google maps js file on component load
      const el: HTMLInputElement | null = document.getElementById('map-source-link') as HTMLInputElement;
      if (el) {
        CommonUtils.injectScript(el.value);
        el.remove();
      }
    });
    // get default configured values from AEM
    const defaultValues = getDefaultValues();
    // for client side rendering, get last success search form data from API
    if (staticContent['submitAction'] === PUBLISH_USER_SELECTION && document.cookie) {
      setCookieValues();
    }
    // if last searched data available use it to initialize form
    /**
     * initiate form data in input either default or session data
     */
    setInitialStoreValues(formData?.[DESTINATION]?.displayText ? formData : getInitialStoreValues(defaultValues));
    setFormValues(resetForm());

    //avoiding updation of recent searches for ADF as recently viewed are already getting updated for ADF
    if (formData?.[DESTINATION]?.displayText && !staticContent?.enableAdfSearchForm) {
      updateRecentSearched(formData);
    }

    const sessionObject = {
      data: sessionData?.data ? sessionData?.data : sessionData?.cacheData?.data,
    };

    if (formData) {
      if (formData?.dates?.lengthOfStay) {
        setNightCount(formData?.dates?.lengthOfStay);
      } else {
        setNightCount(1);
      }
      // determine if flexible or regular search based on session data
      const isFlexibleSearchType =
        sessionObject?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkInDate &&
        sessionObject?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkOutDate &&
        sessionObject?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.flexibleDate === true
          ? true
          : !sessionObject?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkInDate &&
            !sessionObject?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkOutDate
          ? true
          : false;
      const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;
      if (shouldUseZustand) {
        searchFormRenderedDataStore.setTabSelected(
          isFlexibleSearchType || staticContent?.enableAdfSearchForm
            ? staticContent['flexibleTabText']
            : staticContent['exactDatesTabText']
        );
      }
      //to select the tab based on session & AEM Flags
      setTabSelected(
        isFlexibleSearchType || staticContent?.enableAdfSearchForm
          ? staticContent['flexibleTabText']
          : staticContent['exactDatesTabText']
      );

      setYearSelected(
        Number(moment(formData?.dates?.fromDate)?.format('YYYY'))
          ? Number(moment(formData?.dates?.fromDate)?.format('YYYY'))
          : Number(moment().format('YYYY'))
      );

      // eslint-disable-next-line no-console
      log.debug('session data in search form', formData?.dates, formData?.dates?.fromDate);
    }
    setShouldCallSuggestedPlaceDetails(true);
    setFormValues(
      setDestinationHiddenFields({
        city: sessionObject?.data?.AriesSearch?.searchCriteria?.address?.city,
        country: sessionObject?.data?.AriesSearch?.searchCriteria?.address?.country,
        latitude: sessionObject?.data?.AriesSearch?.searchCriteria?.address?.latitude,
        longitude: sessionObject?.data?.AriesSearch?.searchCriteria?.address?.longitude,
        state: sessionObject?.data?.AriesSearch?.searchCriteria?.address?.stateProvince,
        radius: parseFloat(sessionObject?.data?.AriesSearch?.searchCriteria?.searchRadius),
        region: sessionObject?.data?.AriesSearch?.searchCriteria?.address?.region,
        destinationType: sessionObject?.data?.AriesSearch?.searchCriteria?.address?.destinationType,
      })
    );
    // update fromDate and toDate search based on session data
    let sessionFromDate = sessionObject?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkInDate;
    let sessionToDate = sessionObject?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.checkOutDate;
    sessionFromDate = sessionFromDate ? moment(sessionFromDate) : sessionFromDate;
    sessionToDate = sessionToDate ? moment(sessionToDate) : sessionToDate;
    setFormValues(
      setDates({
        fromDate: sessionFromDate ? sessionFromDate.subtract(sessionFromDate.utcOffset() / 60, 'hours') : moment(),
        toDate: sessionToDate
          ? sessionToDate.subtract(sessionToDate.utcOffset() / 60, 'hours')
          : moment().add(1, 'days'),
      })
    );
  }, []);

  useEffect(() => {
    if (formData?.destination?.pid === '') {
      setFormValues(
        setDestination({
          displayText: formData?.destination?.displayText,
          pid: '',
        })
      );
    } else if (formData?.destination?.pid !== '' && formData?.destination?.pid !== formValues?.destination?.pid) {
      setFormValues(
        setDestination({
          displayText: formData?.destination?.displayText,
          pid: formData?.destination?.pid,
        })
      );
    }
  }, [formData?.destination?.displayText]);

  // Listen for retry-to-LRR event so formValues (and mobile modal) stay in sync when retry happens on page load.
  // SpecialRates/Form only mount when modal is open, so we must handle the event at this level.
  useEffect(() => {
    const handleResetToLowestRegularRate = (): void => {
      const lowestRatesLabel =
        (
          staticContent?.specialRatesListItems as Array<{ specialRatesCode?: string; specialRateslabel?: string }>
        )?.find(item => item?.specialRatesCode === 'none')?.specialRateslabel ?? 'Lowest Regular Rate';
      setFormValues(
        setSpecialRates({
          displayText: lowestRatesLabel,
          clusterCode: 'none',
          corporateCode: '',
        })
      );
    };
    eventUtil.on('resetSpecialRatesToLowestRegularRate', handleResetToLowestRegularRate);
    return (): void => {
      eventUtil.remove('resetSpecialRatesToLowestRegularRate', () => {});
    };
  }, []);

  // if change in destination pid, fetch destination hidden values
  useEffect(() => {
    if (shouldCallSuggestedPlaceDetails) {
      const pid = formValues?.[DESTINATION]?.pid;
      const displayText = formValues?.[DESTINATION]?.displayText;
      const oldPid = formData?.[DESTINATION]?.pid;
      const oldDisplayText = formData?.[DESTINATION]?.displayText;
      if (pid && pid !== oldPid && displayText !== oldDisplayText) {
        // update hidden fields for selected destination
        try {
          setSuggestedPlaceDetailsCallDone(false);

          if (!skipQuery) {
            getSuggestedPlaceDetails({
              variables: {
                placeId: pid,
              },
              context: {
                headers: {
                  'x-request-id': requestId,
                  'accept-language': currentLocale,
                },
              },
            });
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          log.debug(`Error in update loc detail:${inspect(e)}`);
        }
      } else {
        // reset destinationHiddenFields in case if destinationAddressPlaceId, PID and country is not present,
        // Otherwise coutry is getting removed from the url and normal destination search happening, resulting in different resultset and destination field text changing.
        if (
          (!pid || pid === '') &&
          !formValues?.[DESTINATION_HIDDEN_FIELDS]?.placeId &&
          !formValues?.[DESTINATION_HIDDEN_FIELDS]?.country &&
          !formValues?.[DESTINATION_HIDDEN_FIELDS]?.region &&
          !formValues?.[DESTINATION_HIDDEN_FIELDS]?.latitude &&
          !formValues?.[DESTINATION_HIDDEN_FIELDS]?.longitude &&
          !formValues?.[DESTINATION_HIDDEN_FIELDS]?.destinationType
        ) {
          setFormValues(setDestinationHiddenFields({}));
        }
      }
    }
  }, [
    formValues?.[DESTINATION]?.pid,
    formValues?.[DESTINATION]?.displayText,
    formValues?.[DESTINATION_HIDDEN_FIELDS]?.destinationType,
  ]);

  // to handle resize event
  useEffect(() => {
    /**
     * Handler for resize event
     */
    const resizeHandler = (): void => {
      const currentViewIsMobile = CommonUtils.isOnlyMobileView();

      if (!isMobileState) {
        // viewport was not mobile earlier but now its mobile
        if (currentViewIsMobile) {
          setIsMobile(true);
          // setFormValues(resetForm());
        }
      } else {
        // viewport was mobile earlier but now its not
        if (!currentViewIsMobile) {
          setIsMobile(false);
          // setFormValues(resetForm());
        }
      }
    };
    window.addEventListener('resize', resizeHandler, { passive: true });
    return (): void => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [isMobileState, featureFlags]);

  // when user interacts with any field on desktop, form will scroll up so that popup can be easily visible
  useEffect(() => {
    const container = searchContainerRef.current;
    if (isPopupOpen && !isMobileState && container) {
      const topPosition = container.getBoundingClientRect().top;
      if (topPosition && topPosition > SEARCH_SCROLL_TOP_POSITION) {
        ScrollUtils.smoothScroll(0, topPosition - SEARCH_SCROLL_TOP_POSITION);
      }
    }
  }, [isPopupOpen]);

  useEffect(() => {
    if (isUserEditSearchForm && isScrolled) {
      scrollPosition.current = 0;
    }
    if (isUpdateSearchClicked && isScrolled) {
      window.scrollTo(0, 0);
      scrollPosition.current = 0;
      setIsScrolled(false);
      setIsUpdateSearchClicked(false);
      const adfHeader = document.getElementsByClassName('m-header');
      const searchFormEl = document.getElementById('document_search_form_container_el_ref');
      if (searchFormEl) {
        searchFormEl?.classList.remove('sticky-search-container');
      }
      // Adding postion attribute to override the Marriott Header default style( Position : fixed)
      if (adfHeader?.length > 0 && staticContent?.enableAdfSearchForm) {
        adfHeader[0]?.setAttribute('style', `position : relative !important;`);
      }
    }
    if (isUpdateSearchClicked && isOnLoad) {
      setIsOnLoad(false);
    }
  }, [isUserEditSearchForm, isUpdateSearchClicked, isScrolled]);

  const aemFragmentHeaderHeight = 50;
  const desktopFormTopPadding = 16;
  const scrollThreshold = aemFragmentHeaderHeight + desktopFormTopPadding;
  const handleScroll = () => {
    const searchFormEl = document.getElementById('document_search_form_container_el_ref');
    const serpPageHeaderComp = document.querySelector('.m-header'); // can't remove, as it is coming from AEM script
    const adfHeader = document.getElementsByClassName('m-header');
    const serpPageHeaderTop = serpPageHeaderComp?.getBoundingClientRect()?.top;
    const searchformComp = searchContainerRef.current;
    /**
     * using window.innerWidth instead of useCheckBreakPoint as
     * useCheckBreakpoint is not working properly in this component
     */

    //TODO: to change it to useCheckBreakpoint if when it starts working
    const width = window?.innerWidth;

    /**
     * if the header top position is -45 then add the padding top to the form
     */
    if (
      serpPageHeaderTop === -45 &&
      window.pageYOffset < scrollThreshold &&
      !(width > 767 && width < 992) &&
      !staticContent.enableAdfSearchForm
    ) {
      const calculatedTop = Math.abs(serpPageHeaderTop) + 24;
      searchformComp?.setAttribute('style', `padding-top:${toRem(calculatedTop)}!important`);
    }
    /**
     * if the serpPageHeaderTop is not equal to -45 then remove the style attribute
     */
    if (serpPageHeaderTop !== -45) {
      searchformComp?.removeAttribute('style');
    }
    /**
     * Form will be sticky at top if scrollThreshold is reached.
     */
    if (window.pageYOffset > scrollThreshold && scrollPosition.current < scrollThreshold) {
      scrollPosition.current = window.pageYOffset;
      if (document.getElementsByName('corp')?.[0]?.ariaChecked !== 'true') {
        setIsScrolled(true);
      }
      setIsUserEditSearchForm(false);
      setIsUserInteractWithComp(false);
      /**
       * remove the style attribute
       */
      searchformComp?.removeAttribute('style');
      if (searchFormEl) {
        searchFormEl?.classList.add('sticky-search-container');
      }
    } else if (
      window.pageYOffset < scrollThreshold &&
      scrollPosition.current > scrollThreshold &&
      (window.pageYOffset === 0 || document.querySelector('.m-header')?.getBoundingClientRect().height !== 0) // No dom manipulation
    ) {
      /**
       * Form takes the default position when the offset is less than the scrollThreshold.
       */
      scrollPosition.current = window.pageYOffset;
      setIsScrolled(false);
      /**
       * remove the style attribute
       */
      searchformComp?.removeAttribute('style');
      if (searchFormEl) {
        searchFormEl?.classList.remove('sticky-search-container');
      }
      // Adding postion attribute to override the Marriott Header default style( Position : fixed) while scrolling in ADF Page
      if (adfHeader?.length > 0 && staticContent?.enableAdfSearchForm) {
        adfHeader[0]?.setAttribute('style', `position : relative !important;`);
      }
      if (
        document.querySelector('.header')?.getBoundingClientRect().height === 0 &&
        width > SEARCH_FORM_WIDTH &&
        !staticContent.enableAdfSearchForm
      ) {
        searchformComp?.setAttribute('style', `padding-top:${toRem(66)}!important`);
      }
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const [isClientSideView, setIsClientSideView] = useState(false);
  // TODO: This should be a shared utility, not unique to this component.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClientSideView(true);
    }
  }, []);

  const getComponent = (
    isMobileState: boolean,
    isHideDestination: boolean,
    isPublishUserSelection: boolean,
    isHideFlexibleDates: boolean,
    showDatesOnMobile: boolean,
    isSearchPage: boolean,
    enableTwoSeparateButtonsForDates: boolean,
    isOnLoad: boolean,
    isScrolled: boolean
  ): ReactElement => {
    // on client side keeping only desktop or mobile view
    if (isClientSideView) {
      return isMobileState ? (
        <SearchMobile
          isHideDestinationProps={isHideDestination}
          isPublishUserSelection={isPublishUserSelection}
          showDatesOnMobile={showDatesOnMobile}
          isHideFlexibleDates={isHideFlexibleDates || isSearchPage}
          isSearchPage={isSearchPage}
          enableTwoSeparateButtonsForDates={enableTwoSeparateButtonsForDates}
          sessionToken={sessionToken}
          searchContainerRef={searchContainerRef}
          desktopFormTopPadding={desktopFormTopPadding}
          isADFPage={staticContent.enableAdfSearchForm} //to determine if the page is ADF
          setSearchLoadForm={setSearchLoadForm}
          isBulgariDomain={isBulgariDomain}
        />
      ) : (
        <SearchDesktop
          isHideDestinationProps={isHideDestination}
          isPublishUserSelection={isPublishUserSelection}
          isHideFlexibleDates={isHideFlexibleDates || isSearchPage}
          isSearchPage={isSearchPage}
          enableTwoSeparateButtonsForDates={enableTwoSeparateButtonsForDates}
          sessionToken={sessionToken}
          isOnLoad={isOnLoad}
          isScrolled={isScrolled}
          searchContainerRef={searchContainerRef}
          isADFPage={staticContent.enableAdfSearchForm} //to determine if the page is ADF
          setSearchLoadForm={setSearchLoadForm}
          isBulgariDomain={isBulgariDomain}
        />
      );
    }

    // in SSR, device size is unknown hence keeping both mobile and desktop views and hiding them using CSS
    return (
      <>
        <SearchMobile
          className="mobile-only-show"
          isHideDestinationProps={isHideDestination}
          isPublishUserSelection={isPublishUserSelection}
          isHideFlexibleDates={isHideFlexibleDates || isSearchPage}
          isSearchPage={isSearchPage}
          enableTwoSeparateButtonsForDates={enableTwoSeparateButtonsForDates}
          sessionToken={sessionToken}
          searchContainerRef={searchContainerRef}
          desktopFormTopPadding={desktopFormTopPadding}
          isADFPage={staticContent.enableAdfSearchForm} //to determine if the page is ADF
          setSearchLoadForm={setSearchLoadForm}
          isBulgariDomain={isBulgariDomain}
        />
        <SearchDesktop
          className="mobile-only-hide"
          isHideDestinationProps={isHideDestination}
          isPublishUserSelection={isPublishUserSelection}
          isHideFlexibleDates={isHideFlexibleDates || isSearchPage}
          isSearchPage={isSearchPage}
          enableTwoSeparateButtonsForDates={enableTwoSeparateButtonsForDates}
          sessionToken={sessionToken}
          isOnLoad={isOnLoad}
          isScrolled={isScrolled}
          searchContainerRef={searchContainerRef}
          isADFPage={staticContent?.enableAdfSearchForm} //to determine if the page is ADF
          setSearchLoadForm={setSearchLoadForm}
          isBulgariDomain={isBulgariDomain}
        />
      </>
    );
  };

  const selectedDestinationValues = {
    isAutoSuggestSelected,
    setIsAutoSuggestSelected,
    isRecentSearchSelected,
    setIsRecentSearchSelected,
  };

  const isViewportM = useCheckBreakpoint('viewportM');
  const setSearchResultsState = useSearchResultsStore(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: { setSearchResultsState: any }) => state.setSearchResultsState
  );
  // function to close the modal and reset the loader code
  const closeModal = () => {
    //delete data for property info from store on close of HQV model
    setSearchResultsState([PROPERTY_INFO_DATA], {
      [PROPERTY_INFO_DATA]: { [PROPERTY_INFO_DATA]: {} },
    });
    const modalHeaderComp = document.getElementsByClassName('hqv-modal-content-header')[0] as HTMLElement;
    modalHeaderComp.style.visibility = 'hidden';
    const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
    loaderComp.style.visibility = 'visible';
    loaderComp.style.display = 'block';
    if (!isViewportM) {
      loaderComp.style.display = 'flex';
    }
    const modalDataComp = document.getElementsByClassName('hqv-modal-data')[0] as HTMLElement;
    modalDataComp.style.visibility = 'hidden';
    modalDataComp.style.display = 'none';
    // Use direct Zustand method if available (when feature flag is ON), otherwise use dispatcher
    if (setModalOpenState) {
      setModalOpenState(false);
    } else {
      hqvDispatch({ type: HQV_ACTIONS.MODAL_OPEN_STATE, payload: false });
    }
    document.body.style.height = '100%';
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY) !== 'true') {
      document.body.style.overflow = 'auto';
    }
  };
  useEffect(() => {
    if (staticContent.enableAdfSearchForm?.toString() === 'true') {
      const { AriesSearch, AriesReservation } = sessionData?.cacheData?.data || {};
      const propertyId = AriesSearch?.searchCriteria?.propertyId || AriesReservation?.propertyId;
      // Use direct Zustand method if available (when feature flag is ON), otherwise use dispatcher
      if (setPropertyID) {
        setPropertyID(propertyId);
      } else {
        hqvDispatch({
          type: HQV_ACTIONS.PROPERTY_IDENTITY,
          payload: propertyId,
        });
      }
    }
  }, []);
  useEffect(() => {
    eventUtil.on('showHotelDetailModal', () => {
      // Use direct Zustand method if available (when feature flag is ON), otherwise use dispatcher
      if (setModalOpenState) {
        setModalOpenState(true);
      } else {
        hqvDispatch({ type: HQV_ACTIONS.MODAL_OPEN_STATE, payload: true });
      }
      document.body.style.overflow = 'hidden';
    });
  }, []);

  // function to hide the loader and show the actual content
  // using styling through JS so as to eliminate re-renders
  function showModalContent(flagLocation: string) {
    log.debug(`[hideOrShowModalContent] Flag triggered from ${flagLocation}`);
    const modalHeaderComp = document.getElementsByClassName('hqv-modal-content-header')[0] as HTMLElement;
    modalHeaderComp.style.visibility = 'visible';
    const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
    loaderComp.style.visibility = 'hidden';
    loaderComp.style.display = 'none';
    const modalDataComp = document.getElementsByClassName('hqv-modal-data')[0] as HTMLElement;
    modalDataComp.style.visibility = 'visible';
    modalDataComp.style.display = 'block';
    const modalCloseButton = document.getElementsByClassName('popup-close')[0] as HTMLElement;
    modalCloseButton?.focus();
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    eventUtil.remove('HQVComponentCallCompleted', () => {});
  }

  // useEffect to add an event listener for the custom event
  useEffect(() => {
    eventUtil.on(`HQVComponentCallCompleted`, data => {
      // Checking if the loader is already hidden. If yes, no need to trigger the same code again
      const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
      if (loaderComp.style.visibility !== 'hidden') {
        showModalContent(data);
      }
    });
    return (): void => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      eventUtil.remove('HQVComponentCallCompleted', () => {});
    };
  }, []);

  return (
    <ErrorHandlerAlert alertMessage={staticContent?.['componentErrorMessage']} sessionToken={sessionToken}>
      <renderedData.Provider value={flexibleSearchValues}>
        {/*ContenetContext is to provide authored values from AEM */}
        <ContentContext.Provider value={staticContent}>
          {/* ViewportContext is to check if it's in mobile view */}
          <ViewportContext.Provider value={isMobileState}>
            {/* FocusContext is to set or check which part of search is on focus */}
            <FocusContext.Provider
              value={{
                focusComp,
                setFocusComp,
                isUserInteractWithComp,
                setIsUserInteractWithComp,
                isUserEditSearchForm,
                setIsUserEditSearchForm,
                isUpdateSearchClicked,
                setIsUpdateSearchClicked,
              }}
            >
              {/* FormValuesContext is to manage any form related values */}
              <SuggestedPlaceDetailsContext.Provider
                value={{ suggestedPlaceDetailsCallDone, setSuggestedPlaceDetailsCallDone }}
              >
                <FormValuesContext.Provider
                  value={{ formValues, setFormValues, UUID, setUUID, setIsPopupOpen, activePopup, setActivePopup }}
                >
                  <SelectDestinationNameContext.Provider value={selectedDestinationValues}>
                    {/* This resolves an issue with page jump on scroll when page height is near 100vh. */}
                    {isScrolled && !staticContent?.enableAdfSearchForm ? (
                      <div className="scrolled-placeholder" style={{ height: `${aemFragmentHeaderHeight}px` }}></div>
                    ) : (
                      ''
                    )}

                    {getComponent(
                      isMobileState,
                      staticContent?.['hideDestination'] === `true`,
                      staticContent['submitAction'] === `publish-user-selection`,
                      staticContent?.['hideFlexibleDatesCheckbox'] === `true`,
                      staticContent?.['showDatesOnMobile'] === `true`,
                      staticContent?.['enableMarriottShopPage'] === `true`,
                      staticContent?.['enableMarriottShopPage'] === `true`, // for different date input box (check in and checkout)
                      // staticContent?.enableTwoBoxForHeaderDates === 'true',
                      isOnLoad,
                      isScrolled
                    )}
                    <PageonModal
                      templateURL={NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT}
                      modelReqBody={getCMSTemplateReqBody}
                      hotelQuickViewCacheConfig={hotelQuickViewCacheConfig}
                      modalOpenState={hqvState.modalOpenState}
                      closeModal={closeModal}
                      operationSignatures={operationSignatures}
                      enableApolloUtility={false}
                      aemPath={NEXT_PUBLIC_AEM_PATH}
                      itemPath={ITEM_PATH_ROOT}
                      pageName={PAGE_NAME}
                      getMockPageModel={getMockPageModel}
                      getItemFromPageModel={getItemFromPageModel}
                      respGridUtil={respGridUtil}
                      ErrorHandlerAlert={ErrorHandlerAlert}
                      flexLayout={true}
                    />
                  </SelectDestinationNameContext.Provider>
                </FormValuesContext.Provider>
              </SuggestedPlaceDetailsContext.Provider>
            </FocusContext.Provider>
          </ViewportContext.Provider>
        </ContentContext.Provider>
      </renderedData.Provider>
    </ErrorHandlerAlert>
  );
};

export default SearchFormComponent;
